import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, RefreshCw, Download, MessageSquare, Send, Loader2 } from "lucide-react";
import api from "@/services/api";
import type { Application } from "@/pages/portal/types";
import { useBranding } from "@/context/BrandingContext";
import {
    getCachedAgencyApplicationDetail,
    setCachedAgencyApplicationDetail,
} from "@/services/agencyApplicationDetailCache";

interface ChatMessage {
    id: number;
    external_id: string | null;
    subject: string;
    message: string | null;
    from_field: string | null;
    status: string | null;
    read: boolean;
    created_at: string | null;
}

function formatDate(value: string | undefined | null): string {
    if (!value) return "";
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatSyncedAt(value: string | null | undefined): string {
    if (!value) return "Never";
    const d = new Date(value);
    return isNaN(d.getTime())
        ? value
        : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function ChatPanel({
    applicationNumber,
    applicationExternalId,
}: {
    applicationNumber: string;
    applicationExternalId: string;
}) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const endRef = useRef<HTMLDivElement>(null);

    const fetchMessages = () => {
        setLoading(true);
        api.get("/integrations/hub/messages/", { params: { application_number: applicationNumber } })
            .then((res) => {
                const convs = res.data.conversations || [];
                const conv = convs.find(
                    (c: { application_number?: string; application_external_id?: string }) =>
                        c.application_number === applicationNumber ||
                        (applicationExternalId && c.application_external_id === applicationExternalId)
                );
                setMessages(conv?.messages || []);
            })
            .catch(() => setMessages([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (applicationNumber) fetchMessages();
    }, [applicationNumber]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = async () => {
        const trimmed = text.trim();
        if (!trimmed || sending || !applicationExternalId) return;
        setSending(true);
        setSendError(null);
        try {
            await api.post("/integrations/hub/messages/send/", {
                application_number: applicationNumber,
                application_external_id: applicationExternalId,
                message: trimmed,
            });
            setText("");
            fetchMessages();
        } catch {
            setSendError("Failed to send message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-4">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Messages
            </h4>

            {loading ? (
                <div className="flex items-center justify-center py-10 gap-2 text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <p className="text-xs font-bold">Loading messages…</p>
                </div>
            ) : (
                <div className="flex flex-col" style={{ height: "50vh", minHeight: 320 }}>
                    <div className="flex-1 overflow-y-auto space-y-3 px-1 py-2" style={{ minHeight: 0 }}>
                        {messages.length === 0 && (
                            <p className="text-xs text-slate-400 text-center py-8">No messages yet. Start the conversation below.</p>
                        )}
                        {messages.map((msg) => {
                            const isOutbound = msg.from_field === "External" || msg.from_field === "Outbound";
                            return (
                                <div key={msg.id} className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
                                    <div className="max-w-[80%] space-y-1">
                                        <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                            isOutbound ? "bg-primary text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"
                                        }`}>
                                            {msg.message || msg.subject}
                                        </div>
                                        <p className={`text-[10px] text-slate-400 px-1 ${isOutbound ? "text-right" : "text-left"}`}>
                                            {isOutbound ? "You" : "Institution"} · {msg.created_at ? new Date(msg.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={endRef} />
                    </div>

                    <div className="shrink-0 pt-3 mt-3 border-t border-slate-100 space-y-2">
                        {sendError && <p className="text-xs text-red-500 font-medium">{sendError}</p>}
                        {!applicationExternalId && (
                            <p className="text-xs text-slate-400">Messaging is not available until this application is synced with Salesforce.</p>
                        )}
                        {applicationExternalId && (
                            <>
                                <p className="text-[10px] text-slate-400">{text.length} / 500 characters</p>
                                <div className="flex gap-2 items-end">
                                    <textarea
                                        className="flex-1 rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-primary outline-none px-4 py-3 text-sm resize-none transition focus:ring-2 focus:ring-primary/20"
                                        rows={2}
                                        placeholder="Type here..."
                                        maxLength={500}
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                                    />
                                    <Button
                                        onClick={handleSend}
                                        disabled={sending || !text.trim()}
                                        className="h-10 px-4 rounded-2xl bg-primary hover:bg-[#002b66] font-bold flex items-center gap-1.5 shrink-0"
                                    >
                                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                        {sending ? "" : "Send"}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AgencyApplicationView() {
    const { branding } = useBranding();
    const navigate = useNavigate();
    const { applicationNumber } = useParams<{ applicationNumber: string }>();

    const [application, setApplication] = useState<Application | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSpinnerFadingOut, setIsSpinnerFadingOut] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isForceSyncing, setIsForceSyncing] = useState(false);
    const [forceSyncError, setForceSyncError] = useState<string | null>(null);
    const [showChat, setShowChat] = useState(false);

    const onBack = () => navigate("/partner/applications");

    const applyDetailData = (base: Application, data: Record<string, unknown>): Application => ({
        ...base,
        Application_Status: (data.application_status as string) || base.Application_Status,
        Stage: (data.stage as string) || base.Stage,
        Application_External_ID: (data.application_external_id as string) || base.Application_External_ID || "",
        offer_letter_url: (data.offer_letter_url as string | null) ?? base.offer_letter_url,
        show_offer_letter_on_portal: (data.show_offer_letter_on_portal as boolean) ?? base.show_offer_letter_on_portal,
        acceptance_letter_url: (data.acceptance_letter_url as string | null) ?? base.acceptance_letter_url,
        show_acceptance_letter_on_portal: (data.show_acceptance_letter_on_portal as boolean) ?? base.show_acceptance_letter_on_portal,
        synced_at: (data.synced_at as string | null) ?? base.synced_at,
        summary: (data.summary as Application["summary"]) ?? base.summary,
        Applicant_Name: (data.summary as { fullName?: string } | undefined)?.fullName || base.Applicant_Name,
        Applied_Program_Name: (data.summary as { appliedProgram?: string } | undefined)?.appliedProgram || base.Applied_Program_Name,
        Applied_Term: (data.summary as { appliedTerm?: string } | undefined)?.appliedTerm || base.Applied_Term,
        Updated_At: (data.updated_at as string) || base.Updated_At,
    });

    useEffect(() => {
        if (!applicationNumber) return;
        let cancelled = false;

        // Serve from cache instantly if fetched within the last 30 seconds.
        // This avoids a redundant Salesforce callout when the user navigates
        // back from the applications list.
        const cached = getCachedAgencyApplicationDetail(applicationNumber);
        if (cached) {
            setApplication(cached);
            setIsSpinnerFadingOut(true);
            const cacheTimer = setTimeout(() => { if (!cancelled) setLoading(false); }, 150);
            return () => {
                cancelled = true;
                clearTimeout(cacheTimer);
            };
        }

        let minLoadingComplete = false;

        const minLoadingTimer = setTimeout(() => {
            minLoadingComplete = true;
        }, 500);

        setLoading(true);
        setIsSpinnerFadingOut(false);
        setIsSyncing(true);
        api.get(`/integrations/hub/applications/${applicationNumber}/detail/`)
            .then((res) => {
                if (cancelled) return;
                if (res.data.status === "success") {
                    const blank: Application = {
                        Application_Number: applicationNumber,
                        Application_External_ID: "",
                        Applicant_Name: "",
                        Applied_Program_Name: "",
                        Stage: "",
                        Applied_Term: "",
                        Application_Status: "",
                        Action: "View",
                        Updated_At: "",
                        Date: new Date(),
                    };
                    const built = applyDetailData(blank, res.data);
                    setApplication(built);
                    setCachedAgencyApplicationDetail(applicationNumber, built);
                }
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) {
                    setIsSyncing(false);
                    // Wait for minimum loading time before fading out spinner
                    const waitForMinLoading = () => {
                        if (cancelled) return;

                        if (minLoadingComplete) {
                            setIsSpinnerFadingOut(true);
                            setTimeout(() => {
                                if (!cancelled) setLoading(false);
                            }, 300);
                        } else {
                            setTimeout(waitForMinLoading, 50);
                        }
                    };
                    waitForMinLoading();
                }
            });

        return () => {
            cancelled = true;
            clearTimeout(minLoadingTimer);
        };
    }, [applicationNumber]);

    const handleForceSync = async () => {
        if (!applicationNumber || isForceSyncing) return;
        setIsForceSyncing(true);
        setForceSyncError(null);
        try {
            const res = await api.post(`/integrations/hub/applications/${applicationNumber}/detail/`);
            if (res.data.status === "success") {
                setApplication((prev) => {
                    const updated = prev ? applyDetailData(prev, res.data) : prev;
                    if (updated) setCachedAgencyApplicationDetail(applicationNumber, updated);
                    return updated;
                });
            } else {
                setForceSyncError(res.data.detail || "Sync failed");
            }
        } catch {
            setForceSyncError("Failed to sync with Salesforce");
        } finally {
            setIsForceSyncing(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center gap-4 py-20">
                <div
                    className="transition-opacity duration-300 ease-in-out"
                    style={{ opacity: isSpinnerFadingOut ? 0 : 1 }}
                >
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                </div>
            </div>
        );
    }

    if (!application) {
        return (
            <div className="flex flex-col items-center gap-4 py-20 text-slate-400">
                <FileText className="w-10 h-10" />
                <p className="text-sm font-bold">Application not found.</p>
                <Button variant="outline" size="sm" onClick={onBack}>Back to Applications</Button>
            </div>
        );
    }

    const normalize = (v: string | undefined | null) =>
        (v || "").trim().toLowerCase().replace(/[\s-]+/g, "_");

    const rawStatus = (application.Application_Status || "").trim();
    const fields = [
        { label: "Full Name",         value: application.summary?.fullName || application.Applicant_Name },
        { label: "Email",             value: application.summary?.email },
        { label: "Mobile",            value: application.summary?.mobile },
        { label: "Citizenship",       value: application.summary?.citizenship },
        { label: "Applied Program",   value: application.summary?.appliedProgram || application.Applied_Program_Name },
        { label: "Applied Term",      value: application.summary?.appliedTerm || application.Applied_Term },
        { label: "Degree",            value: application.summary?.degree },
        { label: "Application Stage", value: application.Stage || "—" },
        ...(rawStatus ? [{ label: "Application Status", value: rawStatus.replace(/_/g, " ") }] : []),
        { label: "Last Updated",      value: formatDate(application.Updated_At) },
    ];

    const stageBadgeColor =
        application.Stage === "Evaluation"
            ? "border-blue-100 text-blue-600 bg-blue-50/50"
            : "border-emerald-100 text-emerald-600 bg-emerald-50/50";

    const configuredStages = (branding?.branding?.stages ?? []) as Array<{ label?: string; value?: string; key?: string }>;
    const defaultSteps = [
        { value: "Signed Up",        label: "Signed Up" },
        { value: "Evaluation",       label: "Evaluation" },
        { value: "Offer Letter",     label: "Offer Letter" },
        { value: "Payment",          label: "Payment" },
        { value: "Acceptance Letter",label: "Acceptance Letter" },
        { value: "Registration",     label: "Registration" },
        { value: "Registered",       label: "Registered" },
    ];
    const steps = configuredStages.length > 0
        ? configuredStages.map((s, i) => ({ value: String(s.value ?? s.key ?? i + 1), label: String(s.label ?? s.value ?? s.key ?? `Stage ${i + 1}`) }))
        : defaultSteps;

    const normalizedCurrent = normalize(application.Stage);
    let currentIndex = steps.findIndex((s) => normalize(s.label) === normalizedCurrent || normalize(s.value) === normalizedCurrent);
    if (currentIndex < 0) currentIndex = 0;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 px-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 hover:text-primary font-bold px-2 -ml-2"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Applications
                </Button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">Application Details</h3>
                        <p className="text-xs text-slate-400 font-medium">{application.Application_Number}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowChat((v) => !v)}
                        className={`flex items-center gap-2 font-black text-xs rounded-xl px-4 h-9 ${showChat ? "border-primary text-primary bg-primary/5" : "border-slate-200 text-slate-600 hover:text-primary"}`}
                    >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Messages
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleForceSync}
                        disabled={isForceSyncing}
                        className="flex items-center gap-2 font-black text-xs border-slate-200 text-slate-600 hover:text-primary rounded-xl px-4 h-9"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${isForceSyncing ? "animate-spin" : ""}`} />
                        {isForceSyncing ? "Syncing…" : "Sync with Salesforce"}
                    </Button>
                    <Badge
                        variant="outline"
                        className={`text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest border whitespace-nowrap ${stageBadgeColor}`}
                    >
                        {application.Stage || "—"}
                    </Badge>
                </div>
            </div>

            {showChat && applicationNumber && (
                <ChatPanel
                    applicationNumber={applicationNumber}
                    applicationExternalId={application.Application_External_ID || ""}
                />
            )}

            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Application Stage
                </h4>
                <div className="flex items-center gap-3 flex-wrap">
                    {steps.map((step, i) => {
                        const isActive = i === currentIndex;
                        const isDone = i < currentIndex;
                        return (
                            <div key={step.value} className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black border-2 ${isActive || isDone ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-200 bg-white text-slate-400"}`}>
                                        {i + 1}
                                    </div>
                                    <span className={`text-xs font-bold ${isActive || isDone ? "text-emerald-600" : "text-slate-400"}`}>
                                        {step.label}
                                    </span>
                                </div>
                                {i < steps.length - 1 && (
                                    <div className={`w-8 h-0.5 ${i < currentIndex ? "bg-emerald-400" : "bg-slate-200"}`} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Application Summary
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {fields.map((item) => (
                        <div key={item.label} className="space-y-1">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{item.label}</p>
                            <p className="text-sm font-bold text-slate-800">{item.value || "—"}</p>
                        </div>
                    ))}
                </div>

                {application.show_offer_letter_on_portal === true && !!application.offer_letter_url && (
                    <a href={application.offer_letter_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 w-full rounded-xl border border-blue-100 bg-blue-50/40 hover:bg-blue-50/70 px-4 py-3 transition-colors group">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20">
                            <Download className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-slate-800">Download Offer Letter</p>
                            <p className="text-[10px] text-slate-400 font-medium">Click to open PDF</p>
                        </div>
                    </a>
                )}

                {application.show_acceptance_letter_on_portal === true && !!application.acceptance_letter_url && (
                    <a href={application.acceptance_letter_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 w-full rounded-xl border border-emerald-100 bg-emerald-50/40 hover:bg-emerald-50/70 px-4 py-3 transition-colors group">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/20">
                            <Download className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                            <p className="text-xs font-black text-slate-800">Download Acceptance Letter</p>
                            <p className="text-[10px] text-slate-400 font-medium">Click to open PDF</p>
                        </div>
                    </a>
                )}
            </div>

            <div className="flex flex-col items-end gap-1 px-2 pb-2">
                {forceSyncError && <p className="text-[10px] text-red-400">{forceSyncError}</p>}
                <p className="text-[10px] text-slate-400">
                    {isSyncing || isForceSyncing
                        ? "Syncing with Salesforce…"
                        : application.synced_at
                          ? `Last update: ${formatSyncedAt(application.synced_at)}`
                          : ""}
                </p>
            </div>
        </div>
    );
}
